/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Invite Codes Service - Super Admin Invite Codes API
 * ============================================================================
 *
 * Service layer for invite code management API calls.
 *
 * ============================================================================
 */

import { get, post } from '@/services/api/client';
import { ApiResponse, PaginatedResponse, PaginationParams, SpringPageResponse, transformPageResponse } from '@/services/api/types';
import { InviteCode, CreateInviteCodeRequest, InviteCodeStats, InviteCodeStatus } from '@/types/superadmin.types';

const BASE_URL = '/superadmin/invite-codes';

/**
 * Backend invite code response (raw entity fields)
 */
interface BackendInviteCode {
  id: string;
  code: string;
  partnerId?: string;
  description?: string;
  codeType: string;
  status: string;
  maxUses?: number;
  currentUses: number;
  validFrom?: string;
  validUntil?: string;
  discountPercent?: number;
  trialExtensionDays?: number;
  freeMonths?: number;
  totalRevenueGenerated?: number;
  createdAt: string;
}

/**
 * Map backend status to frontend status
 */
function mapInviteCodeStatus(backendStatus: string): InviteCodeStatus {
  switch (backendStatus) {
    case 'ACTIVE': return 'ACTIVE';
    case 'EXPIRED': return 'EXPIRED';
    case 'EXHAUSTED': return 'EXHAUSTED';
    case 'PAUSED':
    case 'DISABLED':
    default: return 'DISABLED';
  }
}

/**
 * Transform backend invite code to frontend format
 */
function transformInviteCode(backend: BackendInviteCode): InviteCode {
  return {
    id: backend.id,
    code: backend.code,
    description: backend.description || '',
    partnerId: backend.partnerId,
    partnerName: backend.partnerId ? 'Partner' : undefined, // Would need join to get actual name
    commissionRate: backend.discountPercent || 0,
    maxUses: backend.maxUses,
    currentUses: backend.currentUses || 0,
    expiresAt: backend.validUntil,
    status: mapInviteCodeStatus(backend.status),
    createdAt: backend.createdAt,
    updatedAt: backend.createdAt, // Use createdAt as fallback
  };
}

/**
 * Invite Codes service for Super Admin
 */
export const inviteCodesService = {
  /**
   * Get paginated list of invite codes
   */
  async getInviteCodes(params?: PaginationParams): Promise<PaginatedResponse<InviteCode>> {
    const response = await get<ApiResponse<SpringPageResponse<BackendInviteCode>>>(BASE_URL, params);
    const transformed = transformPageResponse(response.data);
    return {
      ...transformed,
      content: transformed.content.map(transformInviteCode),
    };
  },

  /**
   * Get invite code by ID
   */
  async getInviteCode(id: string): Promise<InviteCode> {
    const response = await get<ApiResponse<BackendInviteCode>>(`${BASE_URL}/${id}`);
    return transformInviteCode(response.data);
  },

  /**
   * Get invite code by code string
   */
  async getInviteCodeByCode(code: string): Promise<InviteCode> {
    const response = await get<ApiResponse<BackendInviteCode>>(`${BASE_URL}/code/${code}`);
    return transformInviteCode(response.data);
  },

  /**
   * Generate a new invite code
   */
  async createInviteCode(data: CreateInviteCodeRequest): Promise<InviteCode> {
    const response = await post<ApiResponse<BackendInviteCode>>(BASE_URL, data);
    return transformInviteCode(response.data);
  },

  /**
   * Deactivate an invite code
   */
  async disableInviteCode(id: string): Promise<void> {
    await post<ApiResponse<void>>(`${BASE_URL}/${id}/deactivate`);
  },

  /**
   * Activate an invite code
   */
  async activateInviteCode(id: string): Promise<void> {
    await post<ApiResponse<void>>(`${BASE_URL}/${id}/activate`);
  },

  /**
   * Get invite codes by partner
   */
  async getInviteCodesByPartner(partnerId: string): Promise<InviteCode[]> {
    const response = await get<ApiResponse<BackendInviteCode[]>>(`${BASE_URL}/partner/${partnerId}`);
    return response.data.map(transformInviteCode);
  },

  /**
   * Get invite codes by status
   */
  async getInviteCodesByStatus(status: string): Promise<InviteCode[]> {
    const response = await get<ApiResponse<BackendInviteCode[]>>(`${BASE_URL}/status/${status}`);
    return response.data.map(transformInviteCode);
  },

  /**
   * Get invite code statistics
   * Note: Computed client-side as backend doesn't have stats endpoint
   */
  async getInviteCodeStats(): Promise<InviteCodeStats> {
    // Fetch all codes and compute stats client-side
    const codes = await this.getInviteCodes({ page: 0, size: 1000 });
    const allCodes = codes.content || [];

    const activeCount = allCodes.filter(c => c.status === 'ACTIVE').length;
    const expiredCount = allCodes.filter(c => c.status === 'EXPIRED').length;
    const totalRedemptions = allCodes.reduce((sum, c) => sum + (c.currentUses || 0), 0);
    const partnerCodes = allCodes.filter(c => c.partnerId).length;

    return {
      activeCount,
      expiredCount,
      totalRedemptions,
      partnerCodes,
    };
  },

  /**
   * Validate an invite code
   */
  async validateInviteCode(code: string): Promise<{
    valid: boolean;
    partnerName?: string;
    discountPercent?: number;
  }> {
    const response = await get<ApiResponse<{
      valid: boolean;
      partnerName?: string;
      discountPercent?: number;
    }>>(`${BASE_URL}/validate/${code}`);
    return response.data;
  },
};

export default inviteCodesService;
